import React, { useRef } from "react";
import { savePDF } from "@progress/kendo-react-pdf";
import { Button, CopyValue, ResponsiveFields, Toast } from "../Library";
import moment from "moment";
import { parseTerminalName, StampTo24hr, StampToDate, formatedPrice } from "../../Utils/utils";
import { FiDownload } from "react-icons/fi";
import Logo from "Components/Logo";
import StatusIndicator from "../../Components/Travel/Booking/Components/status";

function FlightTicket({ booking = {} }) {
	const TICKET = useRef(null);

	const downloadPDF = () => {
		savePDF(TICKET.current, {
			paperSize: "auto",
			margin: 50,
			fileName: `Flight Ticket`,
		});
		Toast.success("Ticket Downloaded");
	};

	const { flights, passengers, amount, date, maker, pnr, bookRefNo, currency, travellers, support_phone, support_email, status } = booking;

	const newStatus = status === "PENDING" ? "In-Progress" : status;

	const pax = passengers?.map(({ passenger, guest }) => ({
		passenger,
		guest,
		//Map the passenger to the traveller by matching the first and last name
		...travellers?.find((traveller) => {
			const pax = passenger || guest;
			const normalize = (str) => str?.replace(/\s+/g, " ").trim().toLowerCase();

			return normalize(`${pax?.fName} ${pax?.lName}`) === normalize(`${traveller?.firstName} ${traveller?.lastName}`);}),
	}));
	return (
		<>
			<div ref={TICKET} className="w-full max-w-[900px] m-auto text-sm bg-light rounded-3xl">
				<div className="p-4 sm:p-6 md:p-10 lg:p-12">
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
						<Logo className="mb-0" />
						<Button onClick={downloadPDF} variant="outlined" className="gap-2 btn-sm w-full sm:w-auto">
							<FiDownload /> Download as PDF
						</Button>
					</div>
					<div className="p-4 sm:p-6 text-white rounded-lg bg-dark flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
						<div className="text-lg sm:text-xl">Flight Booking</div>
						<div className="text-base sm:text-lg font-bold break-words">PNR: {pnr}</div>
					</div>
					<div className="gap-6 p-4 sm:p-6 mt-4 bg-white border rounded-lg">
						<div className="mb-6 text-lg font-bold">Booking Details</div>
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
							<div>
								<div className="mb-2 font-bold">Booked On</div>
								<div>{moment.utc(date).format("DD MMM YYYY")}</div>
							</div>
							<div>
								<div className="mb-2 font-bold">Booked by</div>
								<div className="break-words">{maker?.email}</div>
							</div>
							<div className="sm:text-end">
								<div className="mb-2 font-bold">Amount</div>
								<div>
									{currency} {formatedPrice(amount)}
								</div>
							</div>
							{/* <div className="">
								<div className="mb-2 font-bold">Operating Airline</div>
								<div>{flights?.[0]?.segments?.[0]?.operatingAirline}</div>
							</div> */}
							<div>
								<div className="mb-2 font-bold">Booking Reference No.</div>
								<div className="break-all">
									<CopyValue>{bookRefNo}</CopyValue>
								</div>
							</div>
							<div>
								<div className="mb-2 font-bold">Status</div>
								<StatusIndicator status={newStatus} />
							</div>
						</div>
					</div>
					<div className="p-4 sm:p-6 mt-6 bg-white border rounded-lg">
						<div className="text-lg font-bold">Flight Details</div>
						{flights?.map(({ segments }) => {
							return segments.map((segment, j) => {
								const { airline, cabinClass, flightNumber, departureCode, duration, departureAirport, departureTime, arrivalCode, arrivalAirport, arrivalTime, departureTerminal, arrivalTerminal } = segment;
								const stops = segments.slice(1).map((segment) => segment.departureCode);

								return (
									<div key={j} className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
										<div>
											<div className="font-bold">
												{airline}-{flightNumber}
											</div>
											<img src={`https://cdn.travelwings.com/globalassets/images/${airline?.toUpperCase()}.jpg`} alt="Airline" className="w-10 h-10 mt-2 rounded-full" />
											<div className="mt-1 text-xs text-secondary">{cabinClass}</div>
										</div>

										{/* Mobile: show departure + arrival side-by-side */}
										<div className="flex items-start justify-between gap-4 sm:hidden">
											<div className="text-xs">
												<div className="mb-1 text-xl font-bold">{departureCode}</div>
												<div>
													{StampTo24hr(departureTime)}, {StampToDate(departureTime)}
												</div>
												<div className="break-words">
													{parseTerminalName(departureTerminal)}
													<br />
													{departureAirport}
												</div>
											</div>
											<div className="text-xs text-right">
												<div className="mb-1 text-xl font-bold">{arrivalCode}</div>
												<div>
													{StampTo24hr(arrivalTime)}, {StampToDate(arrivalTime)}
												</div>
												<div className="break-words">
													{parseTerminalName(arrivalTerminal)}
													<br />
													{arrivalAirport}
												</div>
											</div>
										</div>

										{/* Desktop/tablet */}
										<div className="hidden sm:block text-xs text-end">
											<div className="mb-2 text-2xl font-bold">{departureCode}</div>
											<div>
												{StampTo24hr(departureTime)}, {StampToDate(departureTime)}
											</div>
											<div className="break-words">
												{parseTerminalName(departureTerminal)}
												<br />
												{departureAirport}
											</div>
										</div>
									<div className="w-full">
										<div className="w-full sm:flex-grow flex-center min-w-0">
											<div className="w-full mt-1 align-center">
												<div className="w-2 h-2 border rounded-full bg-light" />
												<div className="flex-grow border border-dashed" />
												<div className="px-2 py-1 text-xs text-center border rounded-full bg-light whitespace-normal sm:whitespace-nowrap">
													{duration} {stops.length > 0 && ` Via ${stops.join(", ")}`}
												</div>
												<div className="flex-grow border border-dashed" />
												<div className="w-2 h-2 border rounded-full bg-light" />
											</div>
										</div>
										<div className="w-full text-center">
											{segment.operatedByFound && <div className="badge mt-4">
												<span className="text-xs mr-0.5">Operated by :</span>
												<span className="text-xs font-semibold">{segment.operatedByAirlineName}</span>
											</div>}
										</div>
									</div>

										

										<div className="hidden sm:block text-xs">
											<div className="mb-2 text-2xl font-bold">{arrivalCode}</div>
											<div>
												{StampTo24hr(arrivalTime)}, {StampToDate(arrivalTime)}
											</div>
											<div className="break-words">
												{parseTerminalName(arrivalTerminal)}
												<br />
												{arrivalAirport}
											</div>
										</div>
									</div>
								);
							});
						})}
					</div>
					<div className="p-4 sm:p-6 mt-5 bg-white border rounded-lg">
						<div className="text-lg font-bold">{passengers?.length} Traveller(s)</div>
						<ResponsiveFields
							className="mt-4"
							variant="table"
							items={pax || []}
							columnsClassName="sm:grid-cols-4"
							getKey={(t, i) => `${t?.passenger?.email || t?.guest?.email || ""}-${i}`}
							columns={[
								{
									key: "name",
									label: "NAME",
									valueClassName: "font-bold break-words",
									render: (t) => {
										const pax = t.passenger || t.guest;
										return (
											<>
												{pax?.fName} {pax?.lName}
											</>
										);
									},
								},
								{
									key: "gender",
									label: "GENDER",
									valueClassName: "whitespace-nowrap",
									render: (t) => (t.passenger || t.guest)?.gender || "-",
								},
								{
									key: "baggage",
									label: "BAGGAGE",
									render: (t) => {
										const baggages = t?.baggages || [];
										if (baggages.length === 0) return <div className="text-secondary">-</div>;

										return (
										<div className="space-y-1">
											{baggages.map((b, bi) => {
											const count = baggages.length;

											return (
												<div key={bi}>
												{count > 1 && bi === 0 && <span className="font-bold">ONWARD: </span>}
												{count > 1 && bi === count - 1 && <span className="font-bold">RETURN: </span>}

												{typeof b === "string" && b.includes("/") ? (
													<div className="space-y-1">
													{[...b.split("/")].sort((a, b) => {
														if (a.toLowerCase().includes("cabin")) return -1;
														if (b.toLowerCase().includes("cabin")) return 1;
														return 0;
													}).map((part, pi) => {
														const [label, ...rest] = part.split(":");
														const value = rest.join(":").trim();

														const formattedLabel = label
														? label
															.trim()
															.split(" ")
															.map(
																(word) =>
																word.charAt(0).toUpperCase() +
																word.slice(1).toLowerCase()
															)
															.join(" ")
														: "";

														return (
														<div key={pi} className="break-words">
															{formattedLabel ? (
															<>
																<span className="font-medium">
																{formattedLabel}:
																</span>{" "}
																{value}
															</>
															) : (
															part.trim()
															)}
														</div>
														);
													})}
													</div>
												) : (
													<span className="break-words">{b}</span>
												)}
												</div>
											);
											})}
										</div>
										);
									},
									},
								{
									key: "ticket",
									label: "TICKET",
									valueClassName: "whitespace-nowrap",
									render: (t) => {
										const tickets = [...new Set(t?.tickets || [])].join(", ");
										return tickets || "-";
									},
								},
							]}
						/>
					</div>

					<div className="mt-4 space-y-2 text-xs ">
						<h3 className="font-medium">Terms & Conditions</h3>
						<ul className="list-disc list-inside opacity-70">
							<li>We recommend you to make use of TW booking reference number to communicate with Travelwings team about this booking</li>
							<li>Due to traffic situations, we strongly advise that you reach airport 4 hours prior to departure for International flights and 2 hours prior to departure for domestic flights.</li>
							<li>Re-check the passenger name(s) as per the passport/identity proof, departure & arrival date, time, flight number, terminal, baggage details etc.</li>
							<li>In case of International travel, please check your requirements for travel documentation like valid Passport/visa/Ok to Board/travel and medical insurance with the airline and relevant Embassy or Consulate before commencing your journey.</li>
							<li>If you have a connecting flight, you must review the visa requirements for the transit country, including the exemption conditions for visa-free transit.</li>
							<li>The local authorities in certain countries may impose additional taxes (VAT, tourist tax etc.), which generally has to be paid locally.</li>
							<li>Travelwings, cannot be held responsible for any schedule change/flight cancellation by the airline.</li>
							<li>We will not be held responsible for any loss or damage to travelers and his/her belongings due to any accident, theft or other Mishap / Unforeseen circumstances</li>
							<li>Any amendments of the booking will be as per the airline terms and conditions comprising of penalties, fare difference which may change upon subject to availability by the airline.</li>
							<li>Any Cancellation/Refund of booking will be handled from case to case basis depending on the airline terms and conditions.</li>
						</ul>
					</div>

					<div className="mt-4">
						<p className="text-sm">For any amendments, cancellation or ancillary services or support, please contact us using the following information:</p>
						<p>
							<b>Phone</b> {support_phone} <b>Email</b> {support_email}
						</p>
					</div>
				</div>
			</div>
		</>
	);
}

export default FlightTicket;
